import React, { useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { InputDescription, P } from '@components/shared/Typography';
import { HEMP_SVG } from '@constants';
import IconArrow from '@images/arrow.svg';
import SelectedRadio from '@images/selected-radio.svg';
import UnselectedRadio from '@images/unselected-radio.svg';

interface OptionType {
  label: string;
  value: any;
  children?: React.ReactNode;
  disabled?: boolean;
}

interface Props {
  defaultValue: OptionType;
  options: OptionType[];
  style?: any;
  onSelectCallback?: (value?: any) => void;
  hasSelectedIndicator?: boolean;
  hasHempIcon?: boolean;
  heading?: string;
  description?: string;
  placeholder?: string;
  textColor?: string;
}

const Select: React.FC<Props> = ({
  heading,
  description,
  defaultValue,
  style,
  options,
  onSelectCallback,
  hasSelectedIndicator = false,
  hasHempIcon = false,
  placeholder = '',
  textColor = Colors.forestGreen
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState(defaultValue);

  useEffect(() => {
    document.body.addEventListener('click', handleClick, { passive: true });
    return () => {
      document.body.removeEventListener('click', handleClick, true);
    };
  }, []);

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const onOptionSelect = (option: any) => {
    setSelected(option);
    setOpen(false);
    if (onSelectCallback) {
      onSelectCallback(option);
    }
  };

  function handleClick() {
    setTimeout(() => {
      if (open) {
        setOpen(false);
      }
    });
  }

  function handleOpen() {
    setOpen(!open);
  }

  function renderOptions() {
    return options?.map((option) => (
      <React.Fragment key={typeof option.value === 'object' ? JSON.stringify(option.value) : option.value}>
        <Option
          onClick={() => !option.disabled && onOptionSelect(option)}
          $isSelected={option.value === selected?.value}
          $color={textColor}
          $disabled={option.disabled}
          aria-disabled={option.disabled}
          role="listbox-option"
        >
          {hasSelectedIndicator && (option.value === selected?.value ? <SelectedRadio /> : <UnselectedRadio />)}
          <OptionText style={{ color: textColor }}>{t(option.label)}</OptionText>
          {option.img && <img src={option.img} alt={option.label} />}
          {option.children}
        </Option>
      </React.Fragment>
    ));
  }

  return (
    <Container style={style} className={`select-container ${open ? 'open' : ''}`} role="listbox" aria-haspopup="listbox" tabIndex={0}>
      {heading && <PStyled>{heading}</PStyled>}
      {description && <InputDescription style={{ color: Colors.navyBlue }} dangerouslySetInnerHTML={{ __html: description }} />}
      <Current onClick={handleOpen} $open={open} $hasHeading={!!heading} $isCustom={selected?.custom} className="select-current">
        <OptionWrap>
          <OptionText style={{ color: textColor }}>{selected ? selected?.label : <span>{placeholder}</span>}</OptionText>
          {hasHempIcon && selected?.img && <img src={HEMP_SVG} />}
        </OptionWrap>
        <IconArrowStyled open={open} />
      </Current>
      <Options $open={open} $options={options?.length ?? 0} className="select-options">
        {renderOptions()}
      </Options>
    </Container>
  );
};

export default Select;

const Container = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  margin-bottom: 20px;

  font-family: BrownStd;
  font-weight: normal;
  letter-spacing: 0;
  color: ${Colors.forestGreen};

  svg .color-stroke {
    stroke: ${Colors.forestGreen};
  }

  svg .color-fill {
    fill: ${Colors.forestGreen};
  }

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const Current = styled.div<IOptions>`
  width: 100%;
  height: ${({ $isCustom }) => ($isCustom ? 'auto' : '54px')};
  gap: 3px;
  background: ${Colors.cream};
  border: 1px solid ${Colors.paleGold};
  border-radius: 2px;
  display: flex;
  padding: ${({ $isCustom }) => ($isCustom ? '16px 20px' : '0 20px')};
  justify-content: space-between;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
  ${({ $open }: IOptions) => {
    if ($open) {
      return `
        font-weight: bold
      `;
    }
    return '';
  }};
`;

interface IOptions {
  $open: boolean;
  $options?: number;
  $hasHeading?: boolean;
  $isCustom?: boolean;
}

const Options = styled.div<IOptions>`
  position: absolute;
  box-sizing: border-box;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  background: ${Colors.cream};
  height: 0;
  transition: height 0.2s ease-out;
  border-left: 1px solid ${Colors.paleGold};
  border-right: 1px solid ${Colors.paleGold};
  z-index: 2;
  top: 100%;

  ${({ $open, $options }) => {
    if ($open) {
      return `
        display: flex;
        flex-direction: column;
        min-height: ${49 * ($options < 4 ? $options : 4)}px;
        max-height: ${49 * 6}px;
        border-bottom: 1px solid ${Colors.paleGold};
      `;
    }
    return '';
  }};
`;

interface IOption {
  $isSelected: boolean;
  $color: string;
  $disabled?: boolean;
}

const Option = styled.div<IOption>`
  width: 100%;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: 0.8;

  &:hover {
    cursor: pointer;
    opacity: 1;
  }

  svg {
    path {
      fill: ${Colors.navyBlue};
    }
  }

  svg {
    margin-right: 15px;

    .color-stroke {
      stroke: ${({ $color }) => $color};
    }

    circle.color-fill {
      fill: ${({ $color }) => $color};
    }
  }

  border-bottom: 1px solid ${Colors.paleGold};

  &:last-child {
    border-bottom-width: 0;
  }

  ${({ $isSelected }) => {
    if ($isSelected) {
      return `
        background: #FCF3DF
      `;
    }
    return '';
  }}

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const OptionWrap = styled.div`
  display: flex;
  gap: 8px;
`;

const OptionText = styled(P)`
  font-size: inherit;
  line-height: 150%;
  flex: 1;
  text-align: left;

  &.selected {
    font-weight: inherit;
    font-size: inherit;
  }

  span {
    opacity: 0.5;
  }
`;

interface Arrow {
  open: boolean;
}

const IconArrowStyled = styled(IconArrow)`
  width: 12px;
  height: 7px;

  transition: transform 0.2s ease-out;
  transform: ${({ open }: Arrow) => (open ? 'rotate(180deg)' : null)};
`;

const PStyled = styled(P)`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 150%;
  margin-bottom: 0;
  letter-spacing: 0;
  text-align: left;
  color: ${Colors.navyBlue};
`;
